import type {TurnPermissionsPatch} from '@wandb/weave/lib/WbAgent/WbAgentClient';
import {useEffect, useRef} from 'react';

import {
  builtPromptFromStoreContent,
  resolveTurnParent,
} from '../promptAssembly';
import type {AgentChatStore} from '../store/context';
import type {Thread} from '../store/thread';
import {isAgentChatReadonly} from '../useAgentChatReadonly';
import type {AppendAndRunTurn} from '../useAgentChatRuntime';
import {computeNextAction} from './action';
import {queueKey, useQueueStore} from './store';

/**
 * useQueueDrainer drives every thread's queue from one subscriber, iterating
 * all threads (active or backgrounded) and sending each eligible queue head
 * via `appendAndRunTurn`.
 */
export function useQueueDrainer(
  store: AgentChatStore,
  appendAndRunTurn: AppendAndRunTurn
): void {
  const lastRunIdsRef = useRef(new Map<string, string>());
  const lastHandledRunIdsRef = useRef(new Map<string, string>());

  useEffect(() => {
    const lastRunIds = lastRunIdsRef.current;
    const lastHandledRunIds = lastHandledRunIdsRef.current;

    // initiateDrain sends the targeted queue item as a new turn.
    const initiateDrain = (thread: Thread, key: string, targetId: string) => {
      const queueState = useQueueStore.getState();
      const target = queueState.queues[key]?.find(m => m.id === targetId);
      if (!target) {
        return;
      }

      // NOTE: Mark draining before consuming any flag below, so a re-entrant
      // `evaluate` triggered by that write sees the target already draining.
      queueState.markDraining(key, targetId);

      // NOTE: `resumeRequested`/`steerRequested` are consumed here, not in the
      // caller, so an explicit resume or force-send grants exactly one send
      // past the current pause rather than a standing override.
      if (queueState.resumeRequested[key]) {
        queueState.consumeResume(key);
      }
      if (queueState.steerRequested[key]) {
        queueState.consumeSteer(key);
      }

      const {parentId, parentTurnId} = resolveTurnParent(thread);
      const prompt = builtPromptFromStoreContent(target.content);
      void appendAndRunTurn(
        thread.id,
        prompt,
        parentId,
        target.runConfig.custom.agentConfigOverride ?? undefined,
        parentTurnId,
        target.runConfig.custom.permissions as TurnPermissionsPatch | undefined
      ).finally(() => {
        // Scoped to targetId, not "any draining item": a finished run can
        // start the next drain before this send's own promise settles, so the
        // draining slot may already belong to a different in-flight send by
        // the time this runs.
        if (
          useQueueStore
            .getState()
            .queues[key]?.some(m => m.id === targetId && m.draining)
        ) {
          useQueueStore.getState().clearDraining(key);
        }
      });
    };

    const maybeInitiateDrain = (thread: Thread) => {
      const key = queueKey(thread.id);
      const queueState = useQueueStore.getState();
      const queue = queueState.queues[key] ?? [];
      if (queue.length === 0) {
        return;
      }

      // A steer marker forces a specific (possibly non-head) item next. Heal a
      // marker pointing at an already-removed item and fall back to the head,
      // so a dangling marker never freezes normal draining.
      let steeredId: string | undefined = queueState.steerRequested[key];
      if (steeredId != null && !queue.some(m => m.id === steeredId)) {
        queueState.consumeSteer(key);
        steeredId = undefined;
      }
      const target =
        steeredId != null ? queue.find(m => m.id === steeredId) : queue[0];
      if (!target) {
        return;
      }

      // Wait for any in-flight drain; the draining item may not be the head.
      if (queue.some(m => m.draining)) {
        return;
      }

      // A summary-only thread's `status` is a placeholder ('idle'), not a
      // fetched run status; draining against it could send while the real
      // thread is still running. Wait for the authoritative load.
      //
      // TODO: Remove if the thread ever carries status for the active branch.
      if (thread.loadStatus !== 'loaded') {
        return;
      }

      const lastRunId = lastRunIds.get(thread.id);
      const action = computeNextAction({
        status: thread.status,
        pendingQuestions: thread.pendingQuestions != null,
        pendingNetworkAccessRequest: thread.pendingNetworkAccessRequest != null,
        resumeRequested: Boolean(queueState.resumeRequested[key]),
        steerRequested: steeredId != null,
        readonly: isAgentChatReadonly(),
      });
      if (action !== 'drain' || !useQueueStore.isEligible(target.id)) {
        return;
      }

      // A drained run marks its id below so it never drains twice; the next
      // head waits for a new run to end (a new `lastRunId`). A run that instead
      // left the queue paused (a pending question or network request) returns
      // before that mark, so a later resume of the same run still drains. A
      // steer-triggered drain is exempt (steer still records the run below so
      // the guard can't then drain a second item).
      if (
        !steeredId &&
        lastRunId &&
        lastHandledRunIds.get(thread.id) === lastRunId
      ) {
        return;
      }

      if (lastRunId) {
        lastHandledRunIds.set(thread.id, lastRunId);
      }

      initiateDrain(thread, key, target.id);
    };

    const evaluate = () => {
      const {threads, drafts} = store.getState();
      // NOTE: Include drafts, since a first turn that ends before
      // `turn-created` strands its queue on a still-unpromoted thread.
      const allThreads = {...drafts, ...threads};

      // Clean up stale run IDs.

      for (const id of lastRunIds.keys()) {
        if (!allThreads[id]) {
          lastRunIds.delete(id);
        }
      }
      for (const id of lastHandledRunIds.keys()) {
        if (!allThreads[id]) {
          lastHandledRunIds.delete(id);
        }
      }

      // Handle queue drains.

      for (const thread of Object.values(allThreads)) {
        if (thread.currentRunId) {
          lastRunIds.set(thread.id, thread.currentRunId);
        }
        maybeInitiateDrain(thread);
      }
    };

    evaluate();
    const unsubscribeThreads = store.subscribe(evaluate);
    const unsubscribeQueue = useQueueStore.subscribe(evaluate);
    return () => {
      unsubscribeThreads();
      unsubscribeQueue();
    };
  }, [store, appendAndRunTurn]);
}
